// ProductDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductGrid from './productgrid';
import './styles/productdetail.css';

const ProductDetails = ({ products }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('description');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const product = products.find(p => p.id === parseInt(id));
  
  const allImages = product ? [product.image, ...(product.additionalImages || [])] : [];

  // Move useEffect to the top level, before any returns
  useEffect(() => {
    if (!product) return; // Early return inside useEffect is fine
    
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % allImages.length
      );
    }, 5000);

    return () => clearInterval(timer);
  }, [allImages.length, product]); // Add product to dependency array

  if (!product) {
    return <div className="product-not-found">Product not found</div>;
  }

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== parseInt(id))
    .slice(0, 4);

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  const handleZoomClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderDescription = () => {
    if (typeof product.description === 'string') {
      return <p>{product.description}</p>;
    }
    
    if (Array.isArray(product.description)) {
      return (
        <ul>
          {product.description.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
    }
    
    if (typeof product.description === 'object' && product.description !== null) {
      return Object.entries(product.description).map(([section, content]) => {
        if (Array.isArray(content)) {
          return (
            <div key={section} className="description-section">
              <h3>{section.replace(/([A-Z])/g, ' $1').trim()}</h3>
              <ul>
                {content.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          );
        }
        return (
          <div key={section} className="description-section">
            <h3>{section.replace(/([A-Z])/g, ' $1').trim()}</h3>
            <p>{content}</p>
          </div>
        );
      });
    }
    
    return <p>No description available</p>;
  };

  const renderSpecifications = () => {
    if (!product.specifications) {
      return <p>No specifications available</p>;
    }

    return (
      <table>
        <tbody>
          {Object.entries(product.specifications).map(([key, value]) => (
            <tr key={key}>
              <td>{key}</td>
              <td>{typeof value === 'object' ? JSON.stringify(value) : value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="product-details-container">
      <div className="product-main">
        <div className="product-images">
          <div 
            className="main-image-container" 
            onClick={handleZoomClick}
          >
            <img 
              src={allImages[currentImageIndex]} 
              alt={`${product.name} - Main View`}
              className="main-image"
            />
            <span className="image-zoom-hint">Click to zoom</span>
          </div>

          {product.additionalImages?.length > 0 && (
            <div className="small-images">
              {allImages.map((img, index) => (
                <div 
                  key={index}
                  className={`thumbnail-container ${currentImageIndex === index ? 'active' : ''}`}
                  onClick={() => handleThumbnailClick(index)}
                >
                  <img 
                    src={img} 
                    alt={`${product.name} - View ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          )}

          {isModalOpen && (
            <div className="image-modal-overlay" onClick={handleCloseModal}>
              <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                <img 
                  src={allImages[currentImageIndex]}
                  alt={`${product.name} - Zoomed View`}
                  className="modal-image"
                />
                <button className="modal-close-btn" onClick={handleCloseModal}>
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="product-info">
          <h1>{product.name}</h1>
          <div className="product-meta">
            {product.brand && <p>Brand: {product.brand}</p>}
            {product.model && <p>Model: {product.model}</p>}
          </div>
          <button onClick={() => navigate('/ordernow')} className="contact-btn">
            ORDER NOW
          </button>
        </div>
      </div>

      <div className="product-details-tabs">
        <div className="tab-headers">
          <button 
            className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
            onClick={() => setActiveTab('description')}
          >
            Product description
          </button>
          <button 
            className={`tab-btn ${activeTab === 'specifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('specifications')}
          >
            Technical specifications
          </button>
        </div>
        
        <div className="tab-content">
          {activeTab === 'description' && (
            <div className="description-content">
              {renderDescription()}
            </div>
          )}
          
          {activeTab === 'specifications' && (
            <div className="specifications-content">
              {renderSpecifications()}
            </div>
          )}
        </div>
      </div>

      {relatedProducts.length > 0 && (
        <div className="related-products">
          <h2>Related Products</h2>
          <ProductGrid 
            products={relatedProducts}
            layout="grid-4"
            maxItems={4}
            showViewMore={false}
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetails;